import { Element } from "@xmpp/xml"
import {
  ScheduledWorkflow,
  WorkflowZone,
} from "@/reducers/openclawWorkflowsSlice"

export const WORKFLOWS_NODE = "urn:slickshift:account:workflows:0"

class OpenclawWorkflowsParser {
  /** Parse all workflows from a single PubSub item.
   *  itemId = user JID → local workflows for that user
   *  itemId = "global"  → shared workflows
   */
  static parseItem(item: Element): ScheduledWorkflow[] {
    const el = item.getChild("workflows", WORKFLOWS_NODE)
    if (!el) return []

    const itemId = item.getAttr("id") ?? ""
    const scope: ScheduledWorkflow["scope"] =
      itemId === "global" ? "global" : "local"
    // The item id, also "global": the slice replaces and retracts rules by
    // author, so an empty author would keep deleted shared rules on screen.
    const author = itemId

    return el.getChildren("workflow").flatMap((w) => {
      const id = w.getAttr("id")
      const name = w.getAttr("name")
      if (!id || !name) return []

      const s = w.getChild("schedule")
      let zone: WorkflowZone | undefined
      if (s) {
        const zoneEl = s.getChild("zone")
        if (zoneEl) {
          const zKind = zoneEl.getAttr("kind")
          if (zKind === "country") {
            zone = { kind: "country", country: zoneEl.getAttr("country") ?? "" }
          } else if (zKind === "circle") {
            zone = {
              kind: "circle",
              lat: Number(zoneEl.getAttr("lat")),
              lng: Number(zoneEl.getAttr("lng")),
              radius: Number(zoneEl.getAttr("radius")),
            }
          }
        }
      }
      const schedule = s
        ? {
            kind: s.getAttr("kind") || "cron",
            expr: s.getAttr("expr") || undefined,
            everyMs: s.getAttr("every-ms")
              ? Number(s.getAttr("every-ms"))
              : undefined,
            tz: s.getAttr("tz") || undefined,
            // One-shot "at" schedules carry their instant in `ts` (ISO-8601).
            // When present, the workflow fires exactly once at that instant
            // and then auto-pauses — no more recurrence after that.
            ts: s.getAttr("ts") || undefined,
            on: s.getAttr("on") || undefined,
            zone,
            // Stop-event schedules pin a one-shot rule to a single route stop.
            stopId: s.getAttr("stop-id") || undefined,
            stopAction:
              (s.getAttr("stop-action") as "pickup" | "delivery") || undefined,
            targetJid: s.getAttr("target-jid") || undefined,
            // Stop-dwell limit in minutes; an unreadable value stays unset
            // rather than showing a made-up number.
            minDwellMin: /^\d+$/.test(s.getAttr("min-dwell-min") ?? "")
              ? Number(s.getAttr("min-dwell-min"))
              : undefined,
            documentType: s.getAttr("document-type") || undefined,
            onlyWithIssues:
              s.getAttr("only-with-issues") === "true" || undefined,
          }
        : null

      return [
        {
          kind: "cron",
          id,
          name,
          status:
            (w.getAttr("status") as ScheduledWorkflow["status"]) || "active",
          schedule,
          message: w.getChild("payload")?.getChildText("message") || undefined,
          payloadKind: w.getChild("payload")?.getAttr("kind") || undefined,
          scope,
          author,
        },
      ]
    })
  }
}

export default OpenclawWorkflowsParser
